import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import BaseModal from 'components/atoms/base-modal';
import BaseButton from 'components/atoms/baseButton';
import { useTranslation } from 'react-i18next';
import BaseForm from 'components/atoms/base-form';
import { useFormik } from 'formik';
import BaseFormItem from 'components/atoms/base-form/item';
import BaseInput from 'components/atoms/base-input';
import { useAppDispatch } from 'store';
import useAlert from 'infrastructure/hooks/utils/use-alert';

import { manualEnterCallSchema } from './validationSchema';

const ManualEnterCall = ({ isOpen, toggle, patientGuid, isRpmPanel }) => {
  const { t } = useTranslation();
  const {
    connect: { handleConnectPopoutOpen, connectDetails },
    linkedCallsReadings: { setIsPopupOpen },
  } = useAppDispatch();
  const { Alert, showAlert } = useAlert();

  const formik = useFormik({
    initialValues: { phoneNumber: '' },
    enableReinitialize: true,
    validationSchema: manualEnterCallSchema(),
    validateOnChange: true,
    onSubmit: async ({ phoneNumber }) => {
      const details = {
        phone: phoneNumber,
        guid: patientGuid,
        isRpmCall: isRpmPanel,
      };
      connectDetails(details);

      if (isRpmPanel) {
        const linkReadingsToCall = await showAlert({
          title: t('confirm.title'),
          messageTitle: t('confirm.wouldYouLikeToLinkReadingToCall'),
          type: 'primary',
        });

        if (linkReadingsToCall) {
          setIsPopupOpen(true);
          connectDetails(details);
          toggle();
          return; // handleConnectPopoutOpen(true) will be called after select linked readings
        }
      }

      handleConnectPopoutOpen(true);
      toggle();
    },
  });

  const { submitForm, resetForm, isValid, dirty, isSubmitting } = formik;

  const onCancel = () => {
    resetForm();
    toggle();
  };

  useEffect(() => {
    if (!isOpen) resetForm();
  }, [isOpen, resetForm]);

  return (
    <>
      <BaseModal
        title={t('labels.manualEnterCall')}
        open={isOpen}
        onCancel={onCancel}
        withFooter={[
          <BaseButton
            key="call"
            dataCy="manual-call-submit-button"
            label={t('controls.call')}
            onClick={submitForm}
            disabled={!(dirty && isValid) || isSubmitting}
            loading={isSubmitting}
          />,
        ]}
      >
        <BaseForm
          formik={formik}
          withControls
          plaintext={false}
          readonly={false}
        >
          <BaseFormItem
            name="phoneNumber"
            required
            label={t('labels.phoneNumber')}
            isBlackLabel
          >
            <BaseInput
              placeholder={t('placeholders.enterPhoneNumber')}
              allowClear
              maxLength={20}
              value={formik.values.phoneNumber}
              onChange={(value) => formik.setFieldValue('phoneNumber', value)}
              invalid={
                !!formik.errors.phoneNumber && formik.touched.phoneNumber
              }
            />
          </BaseFormItem>
        </BaseForm>
      </BaseModal>
      {Alert}
    </>
  );
};

ManualEnterCall.defaultProps = {
  isOpen: false,
  isRpmPanel: false,
};

ManualEnterCall.propTypes = {
  toggle: PropTypes.func.isRequired,
  patientGuid: PropTypes.string.isRequired,
  isRpmPanel: PropTypes.bool,
  isOpen: PropTypes.bool,
};

export default ManualEnterCall;
